import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Container,
  Input,
  Label,
  Row,
  FormGroup,
  FormFeedback,
  Badge,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { get, post, put } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import secureLocalStorage from "react-secure-storage";
import { showSuccessToast, showErrorToast } from "helpers/toastMessages";

// Designation options mapped to departments
const designationOptions = {
  "Software Development & Engineering": [
    "Chief Technology Officer (CTO)",
    "VP of Engineering",
    "Software Architect",
    "Engineering Manager",
    "Lead Software Engineer",
    "Senior Software Engineer",
    "Software Engineer",
    "Junior Software Engineer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Mobile App Developer",
    "DevOps Engineer",
    "others",
  ],
  "Product Management": [
    "Chief Product Officer (CPO)",
    "VP of Product",
    "Product Manager",
    "Associate Product Manager",
    "Product Owner",
    "others",
  ],
  "Quality Assurance (QA) & Testing": [
    "QA Manager",
    "Senior QA Engineer",
    "QA Engineer",
    "Automation Tester",
    "Manual Tester",
    "others",
  ],
  "UI/UX Design": [
    "UI/UX Director",
    "UX Designer",
    "UI Designer",
    "Graphic Designer",
    "Interaction Designer",
    "others",
  ],
  "IT & Infrastructure": [
    "Chief Information Officer (CIO)",
    "IT Manager",
    "System Administrator",
    "Network Engineer",
    "IT Support Specialist",
    "others",
  ],
  "Data Science & Analytics": [
    "Data Scientist",
    "Data Engineer",
    "Data Analyst",
    "Business Intelligence Analyst",
    "others",
  ],
  Cybersecurity: [
    "Chief Information Security Officer (CISO)",
    "Security Engineer",
    "Security Analyst",
    "Ethical Hacker",
    "others",
  ],
  "Sales & Business Development": [
    "Chief Revenue Officer (CRO)",
    "VP of Sales",
    "Sales Manager",
    "Business Development Manager",
    "Account Executive",
    "Sales Representative",
    "others",
  ],
  "Marketing & Digital Growth": [
    "Chief Marketing Officer (CMO)",
    "VP of Marketing",
    "Digital Marketing Manager",
    "SEO Specialist",
    "Content Marketer",
    "Social Media Manager",
    "others",
  ],
  "Human Resources (HR)": [
    "Chief Human Resources Officer (CHRO)",
    "HR Manager",
    "Talent Acquisition Specialist",
    "HR Executive",
    "others",
  ],
  "Finance & Accounting": [
    "Chief Financial Officer (CFO)",
    "Finance Manager",
    "Accountant",
    "Financial Analyst",
    "others",
  ],
  "Customer Support & Success": [
    "VP of Customer Success",
    "Customer Support Manager",
    "Technical Support Engineer",
    "Customer Success Manager",
    "others",
  ],
};

// Validation Schema
const validationSchema = Yup.object().shape({
  templateName: Yup.string().required("Template Name is required"),
  jobTitle: Yup.string().required("Job Title is required"),
  department: Yup.string().required("Department is required"),
  designation: Yup.string().required("Designation is required"),
  customDesignation: Yup.string().when("designation", (designation, schema) =>
    designation === "others" ? schema.required("Custom Designation is required") : schema
  ),
  workType: Yup.string().required("Work Type is required"),
  jobLevel: Yup.string().required("Job Level is required"),
  experienceRequired: Yup.string().required("Experience is required"),
  payType: Yup.string().required("Pay Type is required"),
  immediateJoining: Yup.string().required("Immediate Joining is required"),
  dateOfJoining: Yup.date().nullable().when("immediateJoining", (immediateJoining, schema) =>
    immediateJoining === "No" ? schema.required("Date of Joining is required") : schema.nullable()
  ),
  salaryRange: Yup.string().required("Salary Range is required"),
  jobDescription: Yup.string().required("Job Description is required"),
  companyOverview: Yup.string(),
  requiredSkills: Yup.array().min(1, "At least one Required Skill is required"),
  preferredSkills: Yup.array().min(1, "At least one Preferred Skill is required"),
  educationalQualification: Yup.string(),
  companyUrl: Yup.string()
    .url("Must be a valid URL")
    .required("Company URL is required"),
  perksAndBenefits: Yup.string(),
  interviewRounds: Yup.array().min(1, "At least one interview round is required"),
});
 
const AddNewTemplate = () => {
  const navigate = useNavigate();
  const { templateId } = useParams();
 
  const [interviewRounds, setInterviewRounds] = useState([]);
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [preferredSkills, setPreferredSkills] = useState([]);
  const [apiStatus, setApiStatus] = useState({ loading: false, error: "" });
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const employeeEmail = secureLocalStorage.getItem("employeeEmail") || "admin@example.com";
 
  // Fetch template data on mount
  useEffect(() => {
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);
 
  const fetchTemplate = async () => {
    if (!templateId) {
      setApiStatus({ loading: false, error: "No template ID provided" });
      showErrorToast("No template ID provided");
      return;
    }
 
    try {
      setApiStatus({ loading: true, error: "" });
      const response = await get(api.GET_TEMPLATE_BY_ID(templateId));
      console.log("API Response:", response);
 
      if (!response?.data) {
        throw new Error("Invalid template data received");
      }
 
      const templateDetails = response.data;
      const department = templateDetails.department || "";
      setSelectedDepartment(department);
      const isValidDesignation = department && designationOptions[department]?.includes(templateDetails.jobTitle);

      validation.setValues({
        templateName: templateDetails.templateName || "",
        jobTitle: templateDetails.jobTitle || "",
        department: department,
        designation: isValidDesignation ? templateDetails.jobTitle : templateDetails.jobTitle ? "others" : "",
        customDesignation: isValidDesignation ? "" : templateDetails.jobTitle || "",
        workType: templateDetails.workType || "",
        jobLevel: templateDetails.jobLevel || "",
        experienceRequired: templateDetails.experienceRequired || "",
        payType: templateDetails.payType || "",
        immediateJoining: templateDetails.immediateJoining || "No",
        dateOfJoining: templateDetails.dateOfJoining ? new Date(templateDetails.dateOfJoining) : null,
        salaryRange: templateDetails.salaryRange || "",
        jobDescription: templateDetails.jobDescription || "",
        companyOverview: templateDetails.companyOverview || "",
        requiredSkills: templateDetails.requiredSkills || [],
        preferredSkills: templateDetails.preferredSkills || [],
        educationalQualification: templateDetails.educationalQualification || "",
        companyUrl: templateDetails.companyUrl || "",
        perksAndBenefits: templateDetails.perksAndBenefits || "",
        interviewRounds: templateDetails.interviewStages || [],
      });
 
      setInterviewRounds(templateDetails.interviewStages || []);
      setRequiredSkills(templateDetails.requiredSkills || []);
      setPreferredSkills(templateDetails.preferredSkills || []);
      setApiStatus({ loading: false, error: "" });
    } catch (error) {
      console.error("Error fetching template data:", error);
      setApiStatus({ loading: false, error: "Failed to fetch template data" });
      showErrorToast("Failed to fetch template data");
    }
  };
 
  // Handler for interview rounds checkboxes
  const handleInterviewRoundChange = (round, checked) => {
    let updatedRounds;
    if (checked) {
      updatedRounds = [...interviewRounds, round];
    } else {
      updatedRounds = interviewRounds.filter((item) => item !== round);
    }
    setInterviewRounds(updatedRounds);
    validation.setFieldValue("interviewRounds", updatedRounds);
    validation.setFieldTouched("interviewRounds", true);
  };

  // Handler for skills input with comma separation
  const handleSkillsInput = (e, fieldName, setSkills, skills) => {
    const input = e.target.value;
    if (input.includes(',')) {
      const newSkills = input
        .split(',')
        .map(skill => skill.trim())
        .filter(skill => skill && !skills.includes(skill));

      if (newSkills.length > 0) {
        const updatedSkills = [...skills, ...newSkills];
        setSkills(updatedSkills);
        validation.setFieldValue(fieldName, updatedSkills);
        validation.setFieldTouched(fieldName, true);
      }
      e.target.value = '';
    }
  };
 
  const removeSkill = (skillToRemove, fieldName, setSkills, skills) => {
    const updatedSkills = skills.filter((skill) => skill !== skillToRemove);
    setSkills(updatedSkills);
    validation.setFieldValue(fieldName, updatedSkills);
  };
 
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      templateName: "",
      jobTitle: "",
      department: "",
      designation: "",
      customDesignation: "",
      workType: "",
      jobLevel: "",
      experienceRequired: "",
      payType: "",
      immediateJoining: "No",
      dateOfJoining: null,
      salaryRange: "",
      jobDescription: "",
      companyOverview: "",
      requiredSkills: [],
      preferredSkills: [],
      educationalQualification: "",
      companyUrl: "",
      perksAndBenefits: "",
      interviewRounds: [],
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setApiStatus({ loading: true, error: "" });
        const payload = {
          templateDetails: {
            templateId: templateId || "",
            createBy: employeeEmail,
            templateName: values.templateName,
            jobTitle: values.jobTitle,
            department: values.department,
            workType: values.workType,
            jobLevel: values.jobLevel,
            experienceRequired: values.experienceRequired,
            payType: values.payType,
            immediateJoining: values.immediateJoining,
            dateOfJoining: values.dateOfJoining ? values.dateOfJoining.toISOString().split("T")[0] : null,
            salaryRange: values.salaryRange,
            jobDescription: values.jobDescription,
            companyOverview: values.companyOverview,
            requiredSkills: values.requiredSkills,
            preferredSkills: values.preferredSkills,
            educationalQualification: values.educationalQualification,
            companyUrl: values.companyUrl,
            perksAndBenefits: values.perksAndBenefits,
            interviewStages: values.interviewRounds,
          },
        };
 
        if (templateId) {
          const response = await put(api.UPDATE_TEMPLATE_BY_ID(templateId), payload);
          if (response.success) {
            showSuccessToast("Template updated successfully");
            navigate("/template");
          }
        } else {
          const response = await post(api.CREATE_TEMPLATE, payload);
          if (response.success) {
            showSuccessToast("Template created successfully");
            navigate("/template");
          }
        }
        setApiStatus({ loading: false, error: "" });
      } catch (error) {
        console.error("Error submitting form:", error);
        setApiStatus({ loading: false, error: "Something went wrong" });
        showErrorToast("Something went wrong");
      } finally {
        setSubmitting(false);
      }
    },
  });
 
  return (
    <div className="page-content">
      <PageSubHeader
        heading={templateId ? "Edit Template" : "Add New Template"}
        routePath={"Home"}
        pageName={templateId ? "Template / Edit Template" : "Template / Add New Template"}
      />
      <Container fluid className="py-4 px-5">
        {apiStatus.loading && <div>Loading...</div>}
        {apiStatus.error && <div className="text-danger">{apiStatus.error}</div>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          {/* General Info */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">01 - General</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="templateName">
                  Template Name <span className="text-danger">*</span>
                </Label>
                <Input
                  name="templateName"
                  id="templateName"
                  placeholder="Template Name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.templateName}
                  invalid={validation.touched.templateName && !!validation.errors.templateName}
                />
                {validation.touched.templateName && validation.errors.templateName && (
                  <FormFeedback>{validation.errors.templateName}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="jobTitle">
                  Job Title <span className="text-danger">*</span>
                </Label>
                <Input
                  name="jobTitle"
                  id="jobTitle"
                  placeholder="e.g., Software Engineer"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.jobTitle}
                  invalid={validation.touched.jobTitle && !!validation.errors.jobTitle}
                />
                {validation.touched.jobTitle && validation.errors.jobTitle && (
                  <FormFeedback>{validation.errors.jobTitle}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="department">
                  Department <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="department"
                  id="department"
                  onChange={(e) => {
                    validation.handleChange(e);
                    setSelectedDepartment(e.target.value);
                    validation.setFieldValue("designation", "");
                    validation.setFieldValue("customDesignation", "");
                  }}
                  onBlur={validation.handleBlur}
                  value={validation.values.department}
                  invalid={validation.touched.department && !!validation.errors.department}
                >
                  <option value="">Select</option>
                  {Object.keys(designationOptions).map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Input>
                {validation.touched.department && validation.errors.department && (
                  <FormFeedback>{validation.errors.department}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="designation">
                  Designation <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="designation"
                  id="designation"
                  onChange={(e) => {
                    validation.handleChange(e);
                    if (e.target.value !== "others") {
                      validation.setFieldValue("customDesignation", "");
                    }
                  }}
                  onBlur={validation.handleBlur}
                  value={validation.values.designation}
                  invalid={validation.touched.designation && !!validation.errors.designation}
                >
                  <option value="">Select</option>
                  {(designationOptions[selectedDepartment] || []).map((designation) => (
                    <option key={designation} value={designation}>
                      {designation}
                    </option>
                  ))}
                </Input>
                {validation.touched.designation && validation.errors.designation && (
                  <FormFeedback>{validation.errors.designation}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            {validation.values.designation === "others" && (
              <Col md={4}>
                <FormGroup>
                  <Label htmlFor="customDesignation">
                    Custom Designation <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="customDesignation"
                    id="customDesignation"
                    placeholder="Enter custom designation"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.customDesignation}
                    invalid={validation.touched.customDesignation && !!validation.errors.customDesignation}
                  />
                  {validation.touched.customDesignation && validation.errors.customDesignation && (
                    <FormFeedback>{validation.errors.customDesignation}</FormFeedback>
                  )}
                </FormGroup>
              </Col>
            )}
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="workType">
                  Work Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="workType"
                  id="workType"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.workType}
                  invalid={validation.touched.workType && !!validation.errors.workType}
                >
                  <option value="">Select</option>
                  <option>Full-time</option>
                  <option>Part-Time</option>
                  <option>Contract</option>
                  <option>Remote</option>
                  <option>Hybrid</option>
                </Input>
                {validation.touched.workType && validation.errors.workType && (
                  <FormFeedback>{validation.errors.workType}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="jobLevel">
                  Job Level <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="jobLevel"
                  id="jobLevel"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.jobLevel}
                  invalid={validation.touched.jobLevel && !!validation.errors.jobLevel}
                >
                  <option value="">Select</option>
                  <option>Entry-level</option>
                  <option>Mid-level</option>
                  <option>Senior</option>
                  <option>Executive</option>
                </Input>
                {validation.touched.jobLevel && validation.errors.jobLevel && (
                  <FormFeedback>{validation.errors.jobLevel}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="experienceRequired">
                  Experience <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="experienceRequired"
                  id="experienceRequired"
                  placeholder="  Enter Your Experience"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.experienceRequired}
                  invalid={validation.touched.experienceRequired && !!validation.errors.experienceRequired}
                >
                  {/* <option value="">Select</option>
                  <option>Fresher</option>
                  <option>1-2 years</option>
                  <option>2-4 years</option>
                  <option>4-6 years</option>
                  <option>6+ years</option> */}
                </Input>
                {validation.touched.experienceRequired && validation.errors.experienceRequired && (
                  <FormFeedback>{validation.errors.experienceRequired}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="payType">
                  Pay Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="payType"
                  id="payType"
                  placeholder="Pay Rate"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.payType}
                  invalid={validation.touched.payType && !!validation.errors.payType}
                >
                  <option value="">Select</option>
                  <option>Hourly</option>
                  <option>Monthly</option>
                  <option>Yearly</option>
                  <option>Fixed</option>
                </Input>
                {validation.touched.payType && validation.errors.payType && (
                  <FormFeedback>{validation.errors.payType}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="immediateJoining">
                  Immediate Joining <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="immediateJoining"
                  id="immediateJoining"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.immediateJoining}
                  invalid={validation.touched.immediateJoining && !!validation.errors.immediateJoining}
                >
                  <option value="">Select</option>
                  <option>Yes</option>
                  <option>No</option>
                </Input>
                {validation.touched.immediateJoining && validation.errors.immediateJoining && (
                  <FormFeedback>{validation.errors.immediateJoining}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            {validation.values.immediateJoining === "No" && (
              <Col md={4}>
                <FormGroup>
                  <Label htmlFor="dateOfJoining">
                    Date of Joining <span className="text-danger">*</span>
                  </Label>
                  <DatePicker
                    selected={validation.values.dateOfJoining}
                    onChange={(date) => validation.setFieldValue("dateOfJoining", date)}
                    onBlur={() => validation.setFieldTouched("dateOfJoining", true)}
                    dateFormat="yyyy-MM-dd"
                    className={`form-control ${validation.touched.dateOfJoining && validation.errors.dateOfJoining ? "is-invalid" : ""}`}
                    placeholderText="Select Date"
                  />
                  {validation.touched.dateOfJoining && validation.errors.dateOfJoining && (
                    <div className="invalid-feedback">{validation.errors.dateOfJoining}</div>
                  )}
                </FormGroup>
              </Col>
            )}
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="salaryRange">
                  Salary Range <span className="text-danger">*</span>
                </Label>
                <Input
                  name="salaryRange"
                  id="salaryRange"
                  placeholder="e.g., 6-8 LPA, 1000 USD, 600000 INR"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.salaryRange}
                  invalid={validation.touched.salaryRange && !!validation.errors.salaryRange}
                />
                {validation.touched.salaryRange && validation.errors.salaryRange && (
                  <FormFeedback>{validation.errors.salaryRange}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
 
          {/* Job Description */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">02 - Job Description</p>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="jobDescription">
                  Job Description <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="jobDescription"
                  id="jobDescription"
                  placeholder="Type Something..."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.jobDescription}
                  invalid={validation.touched.jobDescription && !!validation.errors.jobDescription}
                />
                <div className="text-muted">
                  {validation.values.jobDescription.length} characters
                </div>
                {validation.touched.jobDescription && validation.errors.jobDescription && (
                  <FormFeedback>{validation.errors.jobDescription}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Required Skills */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">03 - Required Skills</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="requiredSkills">
                  Required Skills (comma-separated) <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="requiredSkills"
                  id="requiredSkills"
                  placeholder="e.g., Java, Python, SQL"
                  onChange={(e) => handleSkillsInput(e, "requiredSkills", setRequiredSkills, requiredSkills)}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.requiredSkills && !!validation.errors.requiredSkills}
                />
                {validation.touched.requiredSkills && validation.errors.requiredSkills && (
                  <FormFeedback>{validation.errors.requiredSkills}</FormFeedback>
                )}
              </FormGroup>
              <div>
                {requiredSkills.map((skill) => (
                  <Badge key={skill} color="primary" className="me-1 mb-2">
                    {skill}
                    <Button
                      close
                      onClick={() => removeSkill(skill, "requiredSkills", setRequiredSkills, requiredSkills)}
                    />
                  </Badge>
                ))}
              </div>
            </Col>
          </Row>
 
          {/* Preferred Skills */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">04 - Preferred Skills</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="preferredSkills">
                  Preferred Skills (comma-separated) <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="preferredSkills"
                  id="preferredSkills"
                  placeholder="e.g., AWS, Docker, React"
                  onChange={(e) => handleSkillsInput(e, "preferredSkills", setPreferredSkills, preferredSkills)}
                  onBlur={validation.handleBlur}
                  invalid={validation.touched.preferredSkills && !!validation.errors.preferredSkills}
                />
                {validation.touched.preferredSkills && validation.errors.preferredSkills && (
                  <FormFeedback>{validation.errors.preferredSkills}</FormFeedback>
                )}
              </FormGroup>
              <div>
                {preferredSkills.map((skill) => (
                  <Badge key={skill} color="primary" className="me-1 mb-2">
                    {skill}
                    <Button
                      close
                      onClick={() => removeSkill(skill, "preferredSkills", setPreferredSkills, preferredSkills)}
                    />
                  </Badge>
                ))}
              </div>
            </Col>
          </Row>
 
          {/* Educational Qualification */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">05 - Educational Qualification</p>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="educationalQualification">
                  Educational Qualification
                </Label>
                <Input
                  type="select"
                  name="educationalQualification"
                  id="educationalQualification"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.educationalQualification}
                  invalid={validation.touched.educationalQualification && !!validation.errors.educationalQualification}
                >
                  <option value="">Select</option>
                  <option>B.E./B.Tech in Computer Science or equivalent</option>
                  <option>B.Sc./M.Sc. in Computer Science</option>
                  <option>Master's Degree</option>
                  <option>Bachelor's Degree</option>
                  <option>Diploma</option>
                </Input>
                {validation.touched.educationalQualification && validation.errors.educationalQualification && (
                  <FormFeedback>{validation.errors.educationalQualification}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
 
          {/* Company Overview */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">06 - Company Overview</p>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="companyOverview">
                  Company Overview
                </Label>
                <Input
                  type="textarea"
                  name="companyOverview"
                  id="companyOverview"
                  placeholder="Type Something..."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.companyOverview}
                  invalid={validation.touched.companyOverview && !!validation.errors.companyOverview}
                />
                {validation.touched.companyOverview && validation.errors.companyOverview && (
                  <FormFeedback>{validation.errors.companyOverview}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
 
          {/* Company URL */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label htmlFor="companyUrl">
                  Company URL <span className="text-danger">*</span>
                </Label>
                <Input
                  name="companyUrl"
                  id="companyUrl"
                  placeholder="https://example.com"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.companyUrl}
                  invalid={validation.touched.companyUrl && !!validation.errors.companyUrl}
                />
                {validation.touched.companyUrl && validation.errors.companyUrl && (
                  <FormFeedback>{validation.errors.companyUrl}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
 
          {/* Perks and Benefits */}
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="perksAndBenefits">
                  Perks and Benefits
                </Label>
                <Input
                  type="textarea"
                  name="perksAndBenefits"
                  id="perksAndBenefits"
                  placeholder="Type Something..."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.perksAndBenefits}
                  invalid={validation.touched.perksAndBenefits && !!validation.errors.perksAndBenefits}
                />
                {validation.touched.perksAndBenefits && validation.errors.perksAndBenefits && (
                  <FormFeedback>{validation.errors.perksAndBenefits}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>
 
          {/* Interview Process */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">07 - Interview Stages</p>
          </Row>
          <Row>
            <p
              style={{
                color: "#555",
                fontWeight: 700,
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              Select the interview rounds <span className="text-danger">*</span>
            </p>
            <Col>
              {[
                "HR Round",
                "Technical Interview",
                "Managerial Round",
                "Offer",
                "Application Screening / Resume Shortlisting",
                "Assignment / Test",
                "Client Round",
              ].map((round) => (
                <FormGroup className="d-flex gap-2" key={round}>
                  <Label
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "22px",
                    }}
                  >
                    <Input
                      className="checkbox me-2"
                      type="checkbox"
                      style={{ border: "1px solid #475467" }}
                      checked={validation.values.interviewRounds.includes(round)}
                      onChange={(e) => handleInterviewRoundChange(round, e.target.checked)}
                    />
                    {round}
                  </Label>
                </FormGroup>
              ))}
              {validation.touched.interviewRounds && validation.errors.interviewRounds && (
                <FormFeedback style={{ display: "block" }}>{validation.errors.interviewRounds}</FormFeedback>
              )}
            </Col>
          </Row>
          <hr />
 
          {/* Form Actions */}
          <Row className="mt-4">
            <Col className="gap-2 d-flex justify-content-end">
              <Button
                className="text-secondary border-secondary"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#475467",
                  borderRadius: "6px",
                  border: "1px solid #475467",
                }}
                onClick={() => navigate("/template")}
              >
                Close
              </Button>
              <Button
                className="text-secondary border-secondary"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#475467",
                  borderRadius: "6px",
                  border: "1px solid #475467",
                }}
                onClick={() => console.log("Preview Template:", validation.values)}
              >
                Preview Template
              </Button>
              <CustomButton
                text={templateId ? "Update Template" : "Save Template"}
                typedfgdfg="submit"
              />
            </Col>
          </Row>
        </form>
      </Container>
    </div>
  );
};
 
export default AddNewTemplate;